import { DialogCloseEvent, DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {ResizeObserver | null} */
  #summaryResizeObserver = null;

  /** @type {MutationObserver | null} */
  #dialogMutationObserver = null;

  /** @type {HTMLElement | null} */
  #observedSummary = null;

  /** @type {number | null} */
  #stickyFrame = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#startStickyMonitoring);
    this.addEventListener(DialogCloseEvent.eventName, this.#stopStickyMonitoring);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#startStickyMonitoring);
    this.removeEventListener(DialogCloseEvent.eventName, this.#stopStickyMonitoring);
    this.#stopStickyMonitoring();
  }

  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open') && !event?.detail?.data?.deferDrawerOpen) {
      this.showDialog();
    }
  };

  #scheduleStickyUpdate = () => {
    if (this.#stickyFrame !== null) return;
    this.#stickyFrame = requestAnimationFrame(() => {
      this.#stickyFrame = null;
      this.#observeCurrentSummary();
      this.#updateStickyState();
    });
  };

  #observeCurrentSummary = () => {
    const dialog = this.refs.dialog;
    const summary = dialog?.querySelector('.cart-drawer__summary');
    if (!(summary instanceof HTMLElement) || summary === this.#observedSummary) return;

    if (this.#observedSummary) this.#summaryResizeObserver?.unobserve(this.#observedSummary);
    this.#observedSummary = summary;
    this.#summaryResizeObserver?.observe(summary);
  };

  #startStickyMonitoring = () => {
    const dialog = this.refs.dialog;
    if (!dialog) return;

    this.#stopStickyMonitoring();
    this.#summaryResizeObserver = new ResizeObserver(this.#scheduleStickyUpdate);
    this.#summaryResizeObserver.observe(dialog);
    this.#observeCurrentSummary();
    this.#dialogMutationObserver = new MutationObserver(this.#scheduleStickyUpdate);
    this.#dialogMutationObserver.observe(dialog, { childList: true, subtree: true });
    window.addEventListener('resize', this.#scheduleStickyUpdate);
    window.visualViewport?.addEventListener('resize', this.#scheduleStickyUpdate);
    document.fonts?.ready.then(this.#scheduleStickyUpdate);
    this.#scheduleStickyUpdate();
  };

  #stopStickyMonitoring = () => {
    this.#summaryResizeObserver?.disconnect();
    this.#summaryResizeObserver = null;
    this.#dialogMutationObserver?.disconnect();
    this.#dialogMutationObserver = null;
    this.#observedSummary = null;
    window.removeEventListener('resize', this.#scheduleStickyUpdate);
    window.visualViewport?.removeEventListener('resize', this.#scheduleStickyUpdate);
    if (this.#stickyFrame !== null) cancelAnimationFrame(this.#stickyFrame);
    this.#stickyFrame = null;
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', 'true');
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
